import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { data, dataOra } from "./text.js";
import { tr } from "./i18n.js";

const PREDA = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR"];

const lei = (v, moneda = "RON") =>
  v === null || v === undefined ? "—"
    : `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}`;

/**
 * MT12.6 / §39 — predarea către facturare.
 *
 * Factura nu se emite aici: o emite programul de contabilitate al firmei, care
 * o trimite mai departe la ANAF. Ecranul ăsta face drumul dintre cele două —
 * ce s-a lucrat și n-a plecat încă, predat ca lot, cu un fișier pe care
 * contabilul îl poate deschide.
 */
export default function Facturare({ eu }) {
  const [sub, setSub] = useState("de-facturat");
  const poatePreda = PREDA.includes(eu.rol);

  return (
    <div className="continut">
      <h2>{tr("Predarea către facturare")}</h2>
      <p className="subtitlu">
        {tr("Ce s-a lucrat și n-a plecat încă la contabilitate. Factura o emite programul de contabilitate — aici se predă lucrarea, o singură dată, cu urmă scrisă.")}
      </p>

      <div className="taburi-sisteme">
        <button className={`tab-sistem ${sub === "de-facturat" ? "activ" : ""}`}
          onClick={() => setSub("de-facturat")}>{tr("De facturat")}</button>
        <button className={`tab-sistem ${sub === "loturi" ? "activ" : ""}`}
          onClick={() => setSub("loturi")}>{tr("Loturi predate")}</button>
      </div>

      {sub === "de-facturat" && <DeFacturat poatePreda={poatePreda} />}
      {sub === "loturi" && <Loturi poatePreda={poatePreda} />}
    </div>
  );
}

// ── Ce e de facturat ───────────────────────────────────────────────────────

function DeFacturat({ poatePreda }) {
  const [clienti, setClienti] = useState([]);
  const [client, setClient] = useState("");
  const [deLa, setDeLa] = useState("");
  const [panaLa, setPanaLa] = useState("");
  const [randuri, setRanduri] = useState(null);
  const [alese, setAlese] = useState(() => new Set());
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  useEffect(() => { api.clienti().then(setClienti).catch(() => {}); }, []);

  const reincarca = useCallback(async () => {
    setRanduri(null); setAlese(new Set());
    try {
      setRanduri(await api.deFacturat({ client: client || undefined,
        deLa: deLa || undefined, panaLa: panaLa || undefined }));
      setEroare("");
    } catch (e) { setEroare(mesajEroare(e)); setRanduri([]); }
  }, [client, deLa, panaLa]);

  useEffect(() => { reincarca(); }, [reincarca]);

  const facturabile = useMemo(
    () => (randuri ?? []).filter((r) => r.deFacturat), [randuri]);
  const acoperite = useMemo(
    () => (randuri ?? []).filter((r) => !r.deFacturat), [randuri]);

  const totalAles = useMemo(() => {
    const set = alese.size ? facturabile.filter((r) => alese.has(r.fisaId)) : facturabile;
    return set.reduce((a, r) => a + r.total, 0);
  }, [alese, facturabile]);

  function comuta(id) {
    const n = new Set(alese);
    if (n.has(id)) n.delete(id); else n.add(id);
    setAlese(n);
  }

  async function preda() {
    if (!client) { setEroare(tr("Alege clientul: un lot se predă pe un singur client.")); return; }
    setLucreaza(true); setMesaj("");
    try {
      const alesIds = alese.size ? [...alese] : undefined;
      const lot = await api.predaLot({
        clientId: client,
        perioadaDeLa: new Date(deLa || "2000-01-01").toISOString(),
        perioadaPanaLa: new Date(panaLa || Date.now()).toISOString(),
        ...(alesIds ? { fiseIds: alesIds } : {}),
      });
      setEroare("");
      setMesaj(tr("Lotul {0} e predat: {1} {2}, {3}. Îl găsești în „Loturi predate\", cu fișierul pentru contabilitate.", lot.numar, lot.numarFise, lot.numarFise === 1 ? "fișă" : "fișe", lei(lot.total, lot.moneda)));
      await reincarca();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  if (randuri === null) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="mesaj">{mesaj}</div>}

      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            <label className="camp">{tr("Client")}
              <select value={client} onChange={(e) => setClient(e.target.value)}>
                <option value="">{tr("toți clienții")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </label>
            <label className="camp">{tr("De la")}
              <input type="date" value={deLa} onChange={(e) => setDeLa(e.target.value)} />
            </label>
            <label className="camp">{tr("Până la")}
              <input type="date" value={panaLa} onChange={(e) => setPanaLa(e.target.value)} />
            </label>
          </div>
          <p className="nota-cautare">
            {tr("Un lot se predă pe UN client: factura se emite pe el, nu pe mai mulți deodată. Fără bife, pleacă tot ce e de facturat în perioadă.")}
          </p>
        </div>
      </div>

      <div className="panou panou-de-facturat">
        <div className="panou-antet">
          <h2>{tr("De facturat")}</h2>
          <span className="numar">{facturabile.length}</span>
          {poatePreda && facturabile.length > 0 && (
            <button className="buton-actiune" disabled={lucreaza} onClick={preda}>
              {lucreaza ? tr("se predă…") : tr("predă {0} · {1}", alese.size ? `${alese.size} fișe` : "tot", lei(totalAles))}
            </button>
          )}
        </div>
        <div className="panou-corp">
          {facturabile.length === 0 ? (
            <div className="gol">
              {tr("Nimic de facturat")}{client ? tr(" la acest client") : ""} {tr("în perioada aleasă.")}
              {acoperite.length > 0 && " Lucrările din perioadă sunt acoperite de garanție sau abonament — vezi mai jos."}
            </div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    {poatePreda && <th />}
                    <th>{tr("Fișă")}</th><th>{tr("Data")}</th><th>{tr("Client")}</th><th>{tr("Obiectiv")}</th>
                    <th>{tr("Fără TVA")}</th><th>TVA</th><th>{tr("Total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {facturabile.map((r) => (
                    <tr key={r.fisaId}>
                      {poatePreda && (
                        <td>
                          <input type="checkbox" checked={alese.has(r.fisaId)}
                            onChange={() => comuta(r.fisaId)}
                            aria-label={tr("alege fișa {0}", r.numar)} />
                        </td>
                      )}
                      <td>{r.numar}<span className="cod-mic">{r.vizita}</span></td>
                      <td>{data(r.data)}</td>
                      <td>{tr(r.client?.denumire)}</td>
                      <td>{r.locatie ?? "—"}</td>
                      <td>{lei(r.totalFaraTVA, r.moneda)}</td>
                      <td>{lei(r.tva, r.moneda)}</td>
                      <td><strong>{lei(r.total, r.moneda)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Lucrările fără valoare NU se ascund: firma trebuie să vadă că a
          lucrat fără să factureze — acolo se pierd banii pe un contract. */}
      {acoperite.length > 0 && (
        <div className="panou">
          <div className="panou-antet">
            <h2>{tr("Lucrate, dar fără valoare de facturat")}</h2>
            <span className="numar">{acoperite.length}</span>
          </div>
          <div className="panou-corp">
            <p className="nota-cautare">
              {tr("Acoperite de garanție sau incluse în abonament. Nu intră în lot, dar au costat — se văd în raportul de rentabilitate.")}
            </p>
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead><tr><th>{tr("Fișă")}</th><th>{tr("Data")}</th><th>{tr("Client")}</th><th>{tr("Obiectiv")}</th></tr></thead>
                <tbody>
                  {acoperite.map((r) => (
                    <tr key={r.fisaId}>
                      <td>{r.numar}<span className="cod-mic">{r.vizita}</span></td>
                      <td>{data(r.data)}</td>
                      <td>{tr(r.client?.denumire)}</td>
                      <td>{r.locatie ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Loturile predate ───────────────────────────────────────────────────────

function Loturi({ poatePreda }) {
  const [lista, setLista] = useState(null);
  const [eroare, setEroare] = useState("");
  const [deschis, setDeschis] = useState(null);
  const [anuleaza, setAnuleaza] = useState(null);

  const reincarca = useCallback(async () => {
    setLista(null);
    try { setLista(await api.loturiFacturare()); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setLista([]); }
  }, []);

  useEffect(() => { reincarca(); }, [reincarca]);

  async function descarca(l) {
    try { await api.descarcaLotCsv(l.id, l.numar); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  if (lista === null) return <div className="gol">{tr("Se încarcă…")}</div>;

  return (
    <>
      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-antet">
          <h2>{tr("Loturi predate")}</h2>
          <span className="numar">{lista.length}</span>
        </div>
        <div className="panou-corp">
          <p className="nota-cautare">
            {tr("Un lot e o predare, nu o factură: poartă amprentă SHA-256, iar conținutul lui nu se mai schimbă. Dacă s-a predat greșit, se anulează cu motiv — fișele lui se întorc singure în lista de facturat.")}
          </p>
          {lista.length === 0 ? (
            <div className="gol">{tr("Niciun lot predat încă.")}</div>
          ) : (
            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr>
                    <th>{tr("Lot")}</th><th>{tr("Client")}</th><th>{tr("Perioadă")}</th><th>{tr("Fișe")}</th>
                    <th>{tr("Total")}</th><th>{tr("Predat")}</th><th>{tr("Factura")}</th><th>{tr("Acțiuni")}</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((l) => (
                    <tr key={l.id} className={l.anulatLa ? "rand-neconform" : ""}>
                      <td>
                        <button className="buton-discret" onClick={() => setDeschis(l.id)}>
                          {l.numar}
                        </button>
                        {l.anulatLa && (
                          <div className="rezultat-context">
                            {tr("anulat de")} {l.anulatDe?.numeComplet ?? "—"}: {l.motivAnulare}
                          </div>
                        )}
                      </td>
                      <td>{tr(l.client?.denumire)}</td>
                      <td>{data(l.perioadaDeLa)} – {data(l.perioadaPanaLa)}</td>
                      <td>{l.numarFise}</td>
                      <td>{lei(l.total, l.moneda)}</td>
                      <td>
                        {dataOra(l.generatLa)}
                        <div className="rezultat-context">{l.generatDe?.numeComplet ?? "—"}</div>
                      </td>
                      <td>
                        {l.referintaFactura
                          ? l.referintaFactura
                          : <span className="marcaj">{tr("încă neemisă")}</span>}
                      </td>
                      <td className="celula-actiuni">
                        <button className="buton-mic" onClick={() => descarca(l)}>CSV</button>
                        {poatePreda && !l.anulatLa && (
                          <button className="buton-mic" onClick={() => setAnuleaza(l)}>{tr("anulează")}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {deschis && (
        <VeziLot id={deschis} poateScrie={poatePreda}
          inchide={() => setDeschis(null)}
          gata={async () => { setDeschis(null); await reincarca(); }} />
      )}
      {anuleaza && (
        <FormAnulare lot={anuleaza} inchide={() => setAnuleaza(null)}
          gata={async () => { setAnuleaza(null); await reincarca(); }} />
      )}
    </>
  );
}

function VeziLot({ id, poateScrie, inchide, gata }) {
  const [l, setL] = useState(null);
  const [referinta, setReferinta] = useState("");
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    api.lotFacturare(id).then((x) => { setL(x); setReferinta(x.referintaFactura ?? ""); })
      .catch(() => setL(false));
  }, [id]);

  async function salveaza() {
    try { await api.scrieReferintaFactura(id, referinta.trim()); await gata(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  if (l === null) return null;
  const c = l?.continut ?? {};

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Lot")} {l?.numar}</h3>
        {l === false ? <div className="eroare">{tr("Lotul nu s-a putut încărca.")}</div> : (
          <>
            {eroare && <div className="eroare">{eroare}</div>}
            <p className="nota-cautare">
              {tr(c.prestator?.denumire)} → {tr(c.client?.denumire)} ·
              {" "}{data(c.perioada?.deLa)} – {data(c.perioada?.panaLa)} ·
              {" "}{tr("predat de")} {c.predat?.de}, {dataOra(c.predat?.la)}
            </p>
            <p className="nota-cautare">
              {tr("Amprentă SHA-256:")} <span className="cod-mic">{l.hashSHA256}</span>
            </p>

            {/* O fișă stornată DUPĂ predare produce o factură greșită. Se
                spune, nu se lasă să treacă neobservată. */}
            {l.stornateDupaPredare?.length > 0 && (
              <div className="panou panou-atentie">
                <div className="panou-corp">
                  <strong>{tr("Atenție:")}</strong> {l.stornateDupaPredare.length}{" "}
                  {l.stornateDupaPredare.length === 1 ? tr("fișă a fost stornată") : tr("fișe au fost stornate")} {tr("după predare (")}{l.stornateDupaPredare.map((f) => f.numar).join(", ")}{tr("). Factura emisă pe lotul ăsta trebuie corectată.")}
                </div>
              </div>
            )}

            <div className="tabel-derulabil">
              <table className="tabel">
                <thead>
                  <tr><th>{tr("Fișă")}</th><th>{tr("Data")}</th><th>{tr("Obiectiv")}</th>
                    <th>{tr("Fără TVA")}</th><th>TVA</th><th>{tr("Total")}</th></tr>
                </thead>
                <tbody>
                  {(c.fise ?? []).map((f, i) => (
                    <Fragment key={i}>
                      <tr>
                        <td>{f.numar}<span className="cod-mic">{f.vizita}</span></td>
                        <td>{data(f.data)}</td>
                        <td>{f.locatie ?? "—"}</td>
                        <td>{lei(f.totalFaraTVA, c.moneda)}</td>
                        <td>{lei(f.tva, c.moneda)}</td>
                        <td>{lei(f.total, c.moneda)}</td>
                      </tr>
                      {/* Liniile, sub fișa lor: exact ce ajunge pe factură. */}
                      {(f.linii ?? []).map((x, j) => (
                        <tr key={`${i}-${j}`} className="linie-deviz">
                          <td colSpan={3}>
                            <span className="cod-mic">{x.fel}</span> {tr(x.denumire)}
                          </td>
                          <td>{Number(x.cantitate).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} × {lei(x.pretUnitar, c.moneda)}</td>
                          <td>{x.cotaTVA ?? "—"}%</td>
                          <td>{lei(x.valoare, c.moneda)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>{tr("Total lot")}</strong></td>
                    <td><strong>{lei(c.totaluri?.totalFaraTVA, c.moneda)}</strong></td>
                    <td><strong>{lei(c.totaluri?.tva, c.moneda)}</strong></td>
                    <td><strong>{lei(c.totaluri?.total, c.moneda)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {poateScrie && !l.anulatLa && (
              <>
                <label className="camp">{tr("Numărul facturii emise în contabilitate")}
                  <input value={referinta} onChange={(e) => setReferinta(e.target.value)}
                    placeholder={tr("ex. FMT 2026 / 138")} />
                </label>
                <p className="nota-cautare">
                  {tr("Aplicația nu-l poate ști singură — factura se emite în alt program. Scris aici, închide bucla: se vede care lucrare a ajuns pe care factură.")}
                </p>
              </>
            )}
          </>
        )}
        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("închide")}</button>
          {poateScrie && l && l !== false && !l.anulatLa && (
            <button className="buton-actiune" onClick={salveaza}>{tr("salvează numărul")}</button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormAnulare({ lot, inchide, gata }) {
  const [motiv, setMotiv] = useState("");
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  async function trimite() {
    setLucreaza(true);
    try { await api.anuleazaLot(lot.id, motiv.trim()); await gata(); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Anulează lotul")} {lot.numar}</h3>
        {eroare && <div className="eroare">{eroare}</div>}
        <p className="nota-cautare">
          {tr("Lotul rămâne în istoric, cu motivul scris. Cele")} {lot.numarFise}{" "}
          {lot.numarFise === 1 ? tr("fișă se întoarce") : tr("fișe se întorc")} {tr("în lista de facturat. Dacă factura a fost deja emisă în contabilitate, ea trebuie corectată acolo — aplicația nu poate face asta.")}
        </p>
        <label className="camp-textarea">{tr("De ce se anulează")}
          <textarea rows={3} value={motiv} onChange={(e) => setMotiv(e.target.value)}
            placeholder={tr("minim 10 caractere")} />
        </label>
        <div className="modal-actiuni">
          <button className="buton-discret" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton-sters" disabled={lucreaza || motiv.trim().length < 10}
            onClick={trimite}>
            {lucreaza ? tr("se anulează…") : tr("anulează lotul")}
          </button>
        </div>
      </div>
    </div>
  );
}
